import Column from '@sportson/core-web/App/layouts/Column';
import Row from '@sportson/core-web/App/layouts/Row';
import ProductPageContainer from '@sportson/core-web/containers/ProductPageContainer';
import { productBreadcrumbs } from '@sportson/core-web/utils/storm';
import Breadcrumbs from 'components/Breadcrumbs';
import { ProductVariantSelector } from 'components/productPage/Variants/ProductVariantSelector';
import { STORM_FILE_IMAGE_TYPES, STORM_FILE_VIDEO_TYPES } from 'config/constants';
import styled from 'libs/styled';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';
import { inServer } from '@sportson/core-web/constants';
import useAppSelector from '@sportson/core-web/hooks/useAppSelector';
import { GetStoresOnhandByPartNo } from '@sportson/core-web/libs/GrebbCommerceAPI/Products';
import getProductRelations from '@sportson/core-web/libs/GrebbCommerceAPI/Products/GetProductRelations';
import CmsModules from '@sportson/core-web/libs/wordpress/content/CmsModules';
import { getMediaUrl } from '@sportson/core-web/utils/getMediaUrl';
import { CompareProductsPopup } from 'components/CompareProducts/CompareProductsPopup';
import { FAQ } from 'components/FAQ';
import Text from 'components/Text';
import ToggleComparison from 'components/inputs/ToggleComparison';
import Accordion from 'components/productPage/accordions/Accordion';
import { filterProductBadges } from 'components/productPage/utils/filterProductBadges';
import ContentGridWrapper from 'components/wrappers/ContentGridWrapper';
import ModuleGridWrapper from 'components/wrappers/ModuleGridWrapper';
import { above } from '@sportson/core-web/utils/mediaqueries';
import MediaGallery from './MediaGallery';
import { ProductInformation } from './ProductInformation';
import { Badges } from './ProductInformation/Badges';
import { RelatedProductsAccessories } from './RelatedProductsAccessories';
import { Parametrics } from './information/Parametrics';
import VariantLinks from './information/VariantLinks';

const Div = styled('div')``;

const GalleryWrapper = styled(Column)`
    max-height: 100%;
    aspect-ratio: 1;
    gap: 16px;
    border-radius: 4px;

    overflow: hidden;
    position: relative;

    background-color: var(--color-neutrals-100);
`;

const GalleryHeader = styled(Row)`
    max-height: 64px;
    justify-content: space-between;
`;

const AccordionWrapper = styled('div')`
    width: 100%;
    display: flex;
    flex-direction: column;

    > div {
        border-top: 1px solid var(--color-neutrals-200);
    }

    > div:last-of-type {
        border-bottom: 1px solid var(--color-neutrals-200);
    }
`;

const VideolyWrapper = styled('div')`
    width: 100%;
    padding: 0 16px;

    ${above['tablet.md']} {
        padding: 0;
    }
`;

const ProductPage = ({
    bestHistoricalPrice,
    product,
    selectedProduct,
    selectedProductVariants,
    totalPricing,
    variants,
    onVariantChange,
    quantity,
    isInStock,
    isQuantityPackage,
    isBookable,
}) => {
    const { t } = useTranslation();
    const lastVariantsArray = useRef([]);

    const [selectedVariantImage, setSelectedVariantImage] = useState(null);

    const { name: productName, variantParametrics } = product;
    const bestHistoricalPriceIncVat = bestHistoricalPrice * totalPricing.vatRate;
    const pageContent =
        useAppSelector(
            ({ application }) => application?.config?.options?.productPage?.contentBuilder?.pageContent,
            shallowEqual,
        ) ?? {};
    const productFlagIdSeed = product?.format?.flags?.map((flag) => flag.id) ?? [];
    const displayableBadgeFlags = useAppSelector(
        ({ application }) => application.config.options?.productPage?.productBadges ?? [],
    );
    const faqData = useAppSelector(({ application }) => application.config.options?.productPage?.faq[0] ?? []);
    const relatedProductsHeading = useAppSelector(
        ({ application }) => application.config.options?.productPage?.relatedProductsHeading,
    );

    const filteredProductFiles = selectedProduct.files.filter((file) => file.code) || [];

    const productParametrics = variantParametrics ?? [];
    const breadcrumbs = [...productBreadcrumbs(product.categories), { permalink: null, title: productName }];
    const [relatedProductAccessories, setRelatedProductAccessories] = useState([]);
    const [stores, setStores] = useState([]);
    const currentStore = useAppSelector(({ ecommerce }) => ecommerce.stores.selected);

    const headerHeight =
        (!inServer && getComputedStyle(document?.documentElement).getPropertyValue('--header-height')) || 0;
    const wrapperRef = useRef({});

    const getStoreOnhand = async (partNo) => {
        try {
            const response = await GetStoresOnhandByPartNo(partNo);
            setStores(response.data[0]?.items ?? []);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        // See todo above
        // if (!hasVariants) {
        //     // TODO: NOTE: Implement label Options from Storm/Norce to CMS and replace hardcoded filterParametricCodes
        //     const filterParametricCodes = {
        //         sizeParametricPrimary: 'ramstorlek',
        //         sizeParametricSecondary: 'storlek',
        //         colorParametric: 'färg',
        //         colorParametricTechnical: 'färg teknisk',
        //     };
        //     setProductDisplayParametrics(filterParametricCodes, productParametrics);
        // }
        getStoreOnhand(product.partNo);
        getProductRelations(product.id).then((response) => {
            const relationType = {};
            const customRelationType = {
                id: -1,
                code: 'custom_pdp_relation_type',
                name: 'Custom PDP relation type',
                description: "This is not a real relation type, it doesn't exist in Norce",
                relations: {
                    item_count: 0,
                    items: [],
                },
                variant_relations: [],
            };

            for (const responseRelationType of response.data) {
                if (
                    // These are the relation types Sportson want to show on the PDP as related products
                    responseRelationType.code === 'Accessory-3' ||
                    responseRelationType.code === 'Accessory-2' ||
                    responseRelationType.code === 'CrossSell' ||
                    responseRelationType.code === 'UpSell'
                ) {
                    const relationItems = responseRelationType.relations.items;
                    if (relationItems.length > 0) {
                        customRelationType.relations.items = [...customRelationType.relations.items, ...relationItems];
                    }
                }
            }
            customRelationType.relations.item_count = customRelationType.relations.items.length;
            setRelatedProductAccessories(customRelationType.relations ?? []);
        });
    }, [product]);

    const getImagesOfAllProducts = () => {
        const files = [];
        const variantsArray = Object.values(selectedProductVariants).filter((i) => i) || [];
        if (variantsArray.length) {
            variantsArray.forEach((variant) => {
                if (product.id === variant.id) {
                    return;
                }

                const variantHasChanged = lastVariantsArray.current.every((v) => v.id !== variant.id);

                const productImage = {
                    id: variant.id,
                    image: variant.format?.image || variant.image,
                    trigger: !!variant.variant_name && !!variantHasChanged,
                };

                if (productImage.image) {
                    files.push(productImage);
                }
            });
        }

        lastVariantsArray.current = variantsArray;
        return files;
    };
    const name = product.description_header || product.name;
    const { image } = selectedProduct.format;
    const isComingSoon = product.statusId === 2;

    const extraFiles = getImagesOfAllProducts();

    const additionalImages = product.files
        .filter((file) => file.code === 'DetailImage')
        .map((file) => ({
            key: file.id,
            title: file.name,
            image: getMediaUrl(file.format.image),
        }));

    const allFiles = [...filteredProductFiles, ...extraFiles];

    const medias = useMemo(
        () =>
            allFiles.reduce(
                (acc, cur, key) => {
                    const { type, name: title, format, path, code } = cur;

                    if (format?.image && STORM_FILE_IMAGE_TYPES.includes(type)) {
                        return [...acc, { key, title, code, image: getMediaUrl(format.image) }];
                    }

                    if (STORM_FILE_VIDEO_TYPES.includes(type)) {
                        if (path) {
                            return [...acc, { key, title, code, youtube: path }];
                        }
                        if (format.image) {
                            return [...acc, { key, title, code, video: getMediaUrl(format.image) }];
                        }
                    }

                    return acc;
                },
                [{ image: getMediaUrl(image), title: name, key: image }],
            ),
        [image, name, selectedVariantImage, allFiles],
    );

    medias.push(...additionalImages);

    const isSlider = medias.length > 1;

    const { commercialBadges, technicalBadges } = filterProductBadges(productFlagIdSeed, displayableBadgeFlags);

    const scrollToTop = (smooth = false) => {
        // Scroll back to top of wrapper on pagination
        if (wrapperRef && wrapperRef.current) {
            scrollTo({
                smooth,
                top: wrapperRef.current.offsetTop - headerHeight,
            });
        }
    };

    useEffect(() => {
        scrollToTop();
    }, []);

    const variantChanged = (productId, productVariant, selectedVariantParametrics) => {
        productVariant && getStoreOnhand(productVariant.partNo);
        onVariantChange(productId, productVariant);
        Array.isArray(selectedVariantParametrics) &&
            !selectedVariantParametrics[1]?.value &&
            getSelectedColorVariantImage(selectedVariantParametrics);
    };

    const getSelectedColorVariantImage = (selectedVariantParametrics) => {
        const selectedColor = selectedVariantParametrics[0].value;
        const selectedColorVariant = product.variants.find((variant) =>
            variant.variantParametrics.find((vParametric) => vParametric.value === selectedColor),
        );
        if (selectedColorVariant) {
            const selectedColorMainImage = {
                key: selectedColorVariant?.imageKey,
                image: getMediaUrl(selectedColorVariant?.format.image),
                title: selectedColorVariant?.name,
            };
            setSelectedVariantImage([selectedColorMainImage]);
        }
    };

    const onVariantNavigation = (productId, productVariant) => {
        scrollToTop();
        variantChanged(productId, productVariant);
    };

    let customProduct = null;
    if (variants.length === 0) {
        // NOTE: This is a workaround to show products without variants with the variant selector visible.
        const colorVariant = product.variantParametrics.find((value) => value.code === 'färg teknisk');
        const sizeVariant = product.variantParametrics.find((value) => value.code === 'ramstorlek');
        const customVariant = [colorVariant, sizeVariant];
        customProduct = { ...product }; // Create a shallow copy of the product object, needed to be able to modify it.
        customProduct.variantParametrics = customVariant;
        if (colorVariant && sizeVariant) {
            variants = [customProduct];
        }
    }

    return (
        <ContentGridWrapper ref={wrapperRef}>
            <Breadcrumbs gridColumn="standard" p={['16px 12px 16px 0', null, null, null]} breadcrumbs={breadcrumbs} />
            <ModuleGridWrapper
                gridColumn={['full-width', null, null, 'standard']}
                gridTemplateColumns={['100%', null, null, '1fr 1fr']}
                columnGap="24px"
                rowGap="20px"
                p={['0', null, null, '0 0 40px']}
            >
                <GalleryWrapper justifyContent={isSlider ? 'space-between' : 'flex-start'}>
                    <GalleryHeader p={['16px 16px 0', null, '16px 24px 0', '16px']}>
                        <Row gap="16px" width="auto" height="fit-content">
                            {/* TODO: BACKLOG: Break out as component implement when like functionality is implemented */}
                            {/* <Button
                                    size="xsmall"
                                    theme="tertiary"
                                    onClick={() => console.log('TODO: Add like functionality')}
                                    aria-label="Add to favorites"
                                >
                                    <HeartIcon />
                                </Button> */}

                            <ToggleComparison
                                size="large"
                                productId={product.partNo.toString()}
                                image={product.imageKey}
                                name={product.name}
                            />
                        </Row>

                        <Row gap="8px" width="auto">
                            {technicalBadges?.length > 0 && <Badges badges={technicalBadges} />}
                        </Row>
                    </GalleryHeader>
                    <MediaGallery
                        media={medias}
                        key={selectedProduct.partNo}
                        files={selectedProduct.files.filter((file) => file.code)}
                        selectedProduct={selectedProduct}
                        productFiles={product.files}
                        variantFiles={product.variants?.reduce(
                            (files, variant) => [...files, ...(variant?.files || [])],
                            [],
                        )}
                    />
                </GalleryWrapper>

                <Div>
                    <ProductInformation
                        product={product}
                        selectedProduct={selectedProduct}
                        selectedProductVariants={selectedProductVariants}
                        totalPricing={totalPricing}
                        isInStock={isInStock}
                        isBookable={isBookable}
                        isQuantityPackage={isQuantityPackage}
                        quantity={quantity}
                        selectedStore={currentStore}
                        storesOnHand={stores}
                        bestHistoricalPriceIncVat={bestHistoricalPriceIncVat}
                        badges={commercialBadges}
                        isComingSoon={isComingSoon}
                    >
                        <ProductVariantSelector
                            key={selectedProductVariants[product.id]?.id || product.id}
                            productId={product.id}
                            variants={variants}
                            callback={variantChanged}
                            selectedVariant={selectedProductVariants[product.id]}
                        />
                    </ProductInformation>
                </Div>
            </ModuleGridWrapper>

            {relatedProductAccessories.item_count > 0 && (
                <ModuleGridWrapper gridColumn={['full-width', null, null, 'standard']} gridTemplateColumns="100%">
                    <RelatedProductsAccessories
                        products={relatedProductAccessories.items}
                        heading={relatedProductsHeading}
                    />
                </ModuleGridWrapper>
            )}

            <ModuleGridWrapper gridColumn={['full-width', null, null, 'standard']} gridTemplateColumns="100%">
                <AccordionWrapper p={['24px 12px', null, null, '40px 0']}>
                    {product?.description && (
                        <Accordion label={t('pdp.info.product_description')}>
                            {product?.description && (
                                <Text
                                    as="span"
                                    typography="Content/18_150_0_350"
                                    dangerouslySetInnerHTML={{ __html: product.description }}
                                />
                            )}
                            {variants.length > 0 && (
                                <VariantLinks
                                    variants={variants}
                                    mainProduct={product}
                                    onVariantChange={onVariantNavigation}
                                />
                            )}
                        </Accordion>
                    )}
                    {productParametrics.length > 0 && <Parametrics parametrics={productParametrics} />}
                </AccordionWrapper>
                {pageContent && <CmsModules data={pageContent} />}

                {faqData && faqData.questions?.length > 0 && (
                    <AccordionWrapper p={['56px 12px', null, '24px 12px', '40px 0']} gap="24px">
                        <FAQ heading={faqData.heading} questions={faqData.questions} />
                    </AccordionWrapper>
                )}
                <VideolyWrapper>
                    <div id="videoly-videobox-placeholder" />
                </VideolyWrapper>
            </ModuleGridWrapper>
            <CompareProductsPopup />
        </ContentGridWrapper>
    );
};

ProductPage.propTypes = {
    bestHistoricalPrice: PropTypes.number,
    isBookable: PropTypes.bool.isRequired,
    isExpired: PropTypes.bool.isRequired,
    isInStock: PropTypes.bool.isRequired,
    isQuantityPackage: PropTypes.bool.isRequired,
    licenseFlag: PropTypes.object,
    onQuantityChange: PropTypes.func.isRequired,
    onVariantChange: PropTypes.func.isRequired,
    product: PropTypes.object.isRequired,
    qtyBreaks: PropTypes.array.isRequired,
    quantity: PropTypes.number.isRequired,
    relations: PropTypes.array.isRequired,
    selectedProduct: PropTypes.object.isRequired,
    selectedProductVariants: PropTypes.object.isRequired,
    totalPricing: PropTypes.object.isRequired,
    variants: PropTypes.array.isRequired,
};

ProductPage.defaultProps = {
    bestHistoricalPrice: null,
    licenseFlag: {},
};

export default () => <ProductPageContainer render={ProductPage} />;
